import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  collectionGroup,
  doc,
  deleteDoc,
  setDoc,
  getDoc,
  updateDoc,
  addDoc,
  // ✅ NEW:
  // (We’ll filter client-side to avoid composite index setup.)
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { useAuth } from "../../../../context/AuthContext";
import { nodeList } from "../../Booking/BookingUtils";

const itemsPerPage = 3; // keep consistent for both panels

const EquipmentOverviewPage = () => {
  const { currentUser } = useAuth();

  const [transactionPageMap, setTransactionPageMap] = useState({});
  const [requestPageMap, setRequestPageMap] = useState({}); // ✅ NEW

  const [equipmentByNode, setEquipmentByNode] = useState({});
  const [nodes, setNodes] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [requests, setRequests] = useState([]); // ✅ NEW (flat list; we’ll group per node in render)

  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddCountModal, setShowAddCountModal] = useState(false);
  const [showReleaseConsumeModal, setShowReleaseConsumeModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false); // ✅ NEW

  const [addCountData, setAddCountData] = useState({
    nodeId: "",
    equipmentId: "",
    currentQuantity: 0,
    name: "",
    quantityToAdd: 1,
  });

  const [transferData, setTransferData] = useState({
    from: "",
    to: "",
    equipmentId: "",
    quantity: 1,
    note: "",
    fromName: "",
    toName: "",
    name: "",
    currentQuantity: 0,
    itemType: "count",
    serialNumber: "",
  });

  const [newEquipment, setNewEquipment] = useState({
    node: "",
    name: "",
    type: "count",
    quantity: 1,
    serialNumber: "",
    note: "",
  });

  // ✅ NEW: request modal state
  const [requestData, setRequestData] = useState({
    nodeId: "",
    name: "",
    itemType: "count",
    quantity: 1,
    note: "",
  });

  const [releaseConsumeData, setReleaseConsumeData] = useState({
    nodeId: "",
    equipmentId: "",
    currentQuantity: 0,
    name: "",
    quantity: 1,
    type: "scrap",
    itemType: "count",
    serialNumber: "",
  });

  // ---------- data loading ----------
  const fetchData = async () => {
    const nodesSnapshot = await getDocs(collection(db, "nodes"));
    var nodeList = nodesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    nodeList.push({
      id: "BDT 1st floor",
      nodename: "BDT 1st floor",
    });
    setNodes(
      nodeList.sort((a, b) => {
        const priority = [
          "Brain Dynamics",
          "BDT 1st floor",
          "Chularat Hospital Group",
          "Ladprao Hospital",
          "Bangmod Hospital",
          "Pat Rangsit Hospital Group",
        ];
        const indexA = priority.indexOf(a.nodename);
        const indexB = priority.indexOf(b.nodename);
        if (indexA === -1 && indexB === -1)
          return a.nodename.localeCompare(b.nodename);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      }),
    );

    const equipmentSnapshot = await getDocs(
      collectionGroup(db, "sleepstudyequipment"),
    );

    const grouped = {};
    equipmentSnapshot.docs.forEach((docSnap) => {
      const equipment = docSnap.data();
      const pathSegments = docSnap.ref.path.split("/");
      const nodeIndex = pathSegments.indexOf("nodes");
      const nodeId = pathSegments[nodeIndex + 1];

      if (!grouped[nodeId]) grouped[nodeId] = [];

      grouped[nodeId].push({ id: docSnap.id, ...equipment });
    });

    // ✅ sort each node’s equipment by name (case-insensitive)
    Object.keys(grouped).forEach((nodeId) => {
      grouped[nodeId].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
    });

    setEquipmentByNode(grouped);

    const transfersSnapshot = await getDocs(
      collection(db, "equipmentTransfers"),
    );
    const transferList = transfersSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setTransfers(
      transferList.sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      ),
    );

    // ✅ NEW: load requests
    const reqSnap = await getDocs(collection(db, "equipmentRequests"));
    const reqList = reqSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    setRequests(
      reqList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
    );
  };

  useEffect(() => {
    fetchData();
  }, [
    showModal,
    showCreateModal,
    showAddCountModal,
    showReleaseConsumeModal,
    showRequestModal,
  ]);
  // ---------- helpers ----------
  const getNodePage = (nodeId) => transactionPageMap[nodeId] || 1;
  const updateNodePage = (nodeId, page) => {
    setTransactionPageMap((prev) => ({ ...prev, [nodeId]: page }));
  };
  const getRequestPage = (nodeId) => requestPageMap[nodeId] || 1; // ✅ NEW
  const updateRequestPage = (nodeId, page) => {
    setRequestPageMap((prev) => ({ ...prev, [nodeId]: page }));
  };

  // ✅ NEW: auto-fulfill any open requests for a node+item when quantity arrives
  const autoFulfillRequests = async ({
    nodeId,
    name,
    itemType,
    addedQuantity, // for serial, pass 1
  }) => {
    if (!nodeId || !name || !itemType || !addedQuantity) return;

    // fetch open requests (client-side filter to avoid index)
    const reqSnap = await getDocs(collection(db, "equipmentRequests"));
    const open = reqSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter(
        (r) =>
          r.nodeId === nodeId &&
          r.name === name &&
          r.itemType === itemType &&
          r.status === "open",
      )
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // FIFO

    let remaining = addedQuantity;

    for (const r of open) {
      if (remaining <= 0) break;
      const already = Number(r.fulfilledQuantity || 0);
      const need = Math.max(0, Number(r.requestedQuantity || 0) - already);
      if (need <= 0) continue;

      const fulfill = itemType === "serial" ? 1 : Math.min(need, remaining);
      await updateDoc(doc(db, "equipmentRequests", r.id), {
        fulfilledQuantity: already + fulfill,
        status:
          already + fulfill >= Number(r.requestedQuantity || 0)
            ? "fulfilled"
            : "open",
      });

      if (itemType !== "serial") {
        remaining -= fulfill;
      } else {
        remaining -= 1;
      }
    }
  };

  // ---------- UI renderers ----------
  const renderEquipment = (item, nodeId) => {
    const isSerial = item.type === "serial";
    const rightText = isSerial
      ? `SN: ${item.serialNumber || "-"}`
      : `Stock: ${Number(item.quantity || 0)}`;

    return (
      <div className="w-100">
        {/* ===== Row 1: name left, type+stock right ===== */}
        <div className="d-flex justify-content-between align-items-start">
          {/* left */}
          <div className="pr-2" style={{ minWidth: 0 }}>
            <div className="font-weight-bold text-truncate">{item.name}</div>
            {item.note ? (
              <div className="small text-muted text-truncate">{item.note}</div>
            ) : null}
          </div>

          {/* right */}
          <div className="text-right" style={{ whiteSpace: "nowrap" }}>
            <span
              className={`badge ${isSerial ? "badge-info" : "badge-success"} mr-2`}
            >
              {isSerial ? "Serial" : "Count"}
            </span>
            <span className="text-muted small">{rightText}</span>
          </div>
        </div>

        {/* ===== Row 2: buttons ===== */}
        <div className="d-flex flex-wrap mt-2" style={{ gap: 6 }}>
          <button
            className="btn btn-sm btn-outline-primary py-0 px-2"
            onClick={() => {
              const nodeObj = nodes.find((n) => n.id === nodeId);
              setTransferData({
                from: nodeId,
                to: "",
                equipmentId: item.id,
                quantity: 1,
                note: "",
                fromName: nodeObj.nodename || "",
                toName: "",
                name: item.name,
                currentQuantity: item.quantity || 0,
                itemType: item.type,
                serialNumber: item.serialNumber || "",
              });
              setShowModal(true);
            }}
          >
            Transfer
          </button>

          {item.type === "count" && (
            <button
              className="btn btn-sm btn-outline-success py-0 px-3"
              onClick={() => {
                setAddCountData({
                  nodeId,
                  equipmentId: item.id,
                  currentQuantity: item.quantity || 0,
                  name: item.name,
                  quantityToAdd: 1,
                });
                setShowAddCountModal(true);
              }}
            >
              Add
            </button>
          )}

          <button
            className="btn btn-sm btn-outline-warning py-0 px-2"
            onClick={() => {
              setReleaseConsumeData({
                nodeId,
                equipmentId: item.id,
                currentQuantity: item.quantity || 1,
                name: item.name,
                quantity: 1,
                type: "scrap",
                itemType: item.type,
                serialNumber: item.serialNumber || "",
                note: "",
              });
              setShowReleaseConsumeModal(true);
            }}
          >
            Scrap / Consume
          </button>

          <button
            className="btn btn-sm btn-outline-danger py-0 px-2"
            onClick={async () => {
              const confirmDelete = window.confirm(
                `Are you sure you want to delete "${item.name}"?`,
              );
              if (!confirmDelete) return;

              const itemRef = doc(
                db,
                `nodes/${nodeId}/sleepstudyequipment/${item.id}`,
              );
              await deleteDoc(itemRef);
              await fetchData();
            }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  // ---------- actions ----------
  const handleTransfer = async () => {
    const { from, to, equipmentId, quantity, note, fromName, toName } =
      transferData;
    if (!from || !to || !equipmentId || from === to || Number(quantity) <= 0)
      return;

    const fromRef = doc(db, `nodes/${from}/sleepstudyequipment/${equipmentId}`);
    const fromSnap = await getDoc(fromRef);
    if (!fromSnap.exists()) return;

    const equipment = fromSnap.data();

    if (equipment.type === "serial") {
      const toRef = doc(db, `nodes/${to}/sleepstudyequipment/${equipmentId}`);
      await setDoc(toRef, equipment);
      await deleteDoc(fromRef);
    } else {
      const toCollection = collection(db, `nodes/${to}/sleepstudyequipment`);
      const toSnapshot = await getDocs(toCollection);

      const existing = toSnapshot.docs.find(
        (doc) =>
          doc.data().name === equipment.name && doc.data().type === "count",
      );

      // decrease in source
      await updateDoc(fromRef, {
        quantity: (equipment.quantity || 0) - Number(quantity),
      });

      if (existing) {
        await updateDoc(existing.ref, {
          quantity: (existing.data().quantity || 0) + Number(quantity),
        });
      } else {
        await addDoc(toCollection, {
          ...equipment,
          quantity: Number(quantity),
          timestamp: new Date().toISOString(),
          createdBy: currentUser.displayName || "",
        });
      }
    }

    // log transfer
    await addDoc(collection(db, "equipmentTransfers"), {
      equipmentId,
      from,
      to,
      quantity: Number(quantity),
      note,
      timestamp: new Date().toISOString(),
      createdBy: currentUser.displayName || "",
      fromName,
      toName,
      name: equipment.name,
    });

    // ✅ NEW: auto-fulfill requests at the destination node
    await autoFulfillRequests({
      nodeId: to,
      name: equipment.name,
      itemType: equipment.type,
      addedQuantity: equipment.type === "serial" ? 1 : Number(quantity),
    });

    setShowModal(false);
    setTransferData({
      from: "",
      to: "",
      equipmentId: "",
      quantity: 1,
      note: "",
      fromName: "",
      toName: "",
      itemType: "count",
      serialNumber: "",
      name: "",
      currentQuantity: 0,
    });

    await fetchData();
  };

  const handleCreateEquipment = async () => {
    const { node, name, type, quantity, serialNumber, note } = newEquipment;
    if (!node || !name || (type === "serial" && !serialNumber)) return;

    const equipmentRef = collection(db, `nodes/${node}/sleepstudyequipment`);
    const snapshot = await getDocs(equipmentRef);

    let createdId = null;
    let addedQtyForRequests = 0;

    if (type === "count") {
      const existing = snapshot.docs.find(
        (doc) => doc.data().name === name && doc.data().type === "count",
      );

      if (existing) {
        await updateDoc(existing.ref, {
          quantity: (existing.data().quantity || 0) + Number(quantity),
        });
        createdId = existing.id;
        addedQtyForRequests = Number(quantity);
      } else {
        const docRef = await addDoc(equipmentRef, {
          name,
          type,
          quantity: Number(quantity),
          serialNumber: "",
          note: note || "",
          timestamp: new Date().toISOString(),
          createdBy: currentUser.displayName || "",
        });
        createdId = docRef.id;
        addedQtyForRequests = Number(quantity);
      }
    } else if (type === "serial") {
      const duplicate = snapshot.docs.find(
        (doc) => doc.data().serialNumber === serialNumber,
      );

      if (duplicate) {
        alert("This serial number already exists in the selected node.");
        return;
      }

      const docRef = await addDoc(equipmentRef, {
        name,
        type,
        quantity: 1,
        serialNumber,
        note: note || "",
        timestamp: new Date().toISOString(),
        createdBy: currentUser.displayName || "",
      });

      createdId = docRef.id;
      addedQtyForRequests = 1;
    }

    if (createdId) {
      await addDoc(collection(db, "equipmentTransfers"), {
        equipmentId: createdId,
        from: "Center",
        to: node,
        quantity: type === "serial" ? 1 : Number(quantity),
        note: "Created",
        timestamp: new Date().toISOString(),
        createdBy: currentUser.displayName || "",
        fromName: "Center",
        toName: nodes.find((n) => n.id === node).nodename || "",
        name: name,
      });

      // ✅ NEW: auto-fulfill requests for this node+item
      await autoFulfillRequests({
        nodeId: node,
        name,
        itemType: type,
        addedQuantity: addedQtyForRequests,
      });
    }

    setShowCreateModal(false);
    setNewEquipment({
      node: "",
      name: "",
      type: "count",
      quantity: 1,
      serialNumber: "",
      note: "",
    });

    await fetchData();
  };

  // ---------- request actions (NEW) ----------
  const openRequestModal = (nodeId) => {
    setRequestData({
      nodeId,
      name: "",
      itemType: "count",
      quantity: 1,
      note: "",
    });
    setShowRequestModal(true);
  };

  const handleCreateRequest = async () => {
    const { nodeId, name, itemType, quantity, note } = requestData;
    if (!nodeId || !name || !itemType) return;
    const qty = itemType === "serial" ? 1 : Number(quantity) || 1;

    await addDoc(collection(db, "equipmentRequests"), {
      nodeId,
      name,
      itemType,
      requestedQuantity: qty,
      fulfilledQuantity: 0,
      status: "open",
      note: note || "",
      timestamp: new Date().toISOString(),
      createdBy: currentUser.displayName || "",
    });

    setShowRequestModal(false);
    await fetchData();
  };

  const cancelRequest = async (reqId) => {
    if (!window.confirm("Cancel this request?")) return;
    await updateDoc(doc(db, "equipmentRequests", reqId), {
      status: "cancelled",
    });
    await fetchData();
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div className="mb-2 mb-md-0">
          <h5 className="mb-0">Sleep Study Consumable</h5>
          <small className="text-muted">
            Manage stock per node, create requests, and track transfers.
          </small>
        </div>

        <button
          className="btn btn-success"
          onClick={() => setShowCreateModal(true)}
        >
          + Add Equipment
        </button>
      </div>

      {nodes.map((node) => {
        const nodeTransfers = transfers.filter(
          (t) => t.from === node.id || t.to === node.id,
        );
        const nodeRequestsOpen = requests.filter(
          (r) => r.nodeId === node.id && r.status === "open",
        );

        // pagination: transfers
        const currentTxPage = getNodePage(node.id);
        const totalTxPages = Math.max(
          1,
          Math.ceil(nodeTransfers.length / itemsPerPage),
        );
        const paginatedTransfers = nodeTransfers.slice(
          (currentTxPage - 1) * itemsPerPage,
          currentTxPage * itemsPerPage,
        );

        // pagination: requests (re-enabled cleanly)
        const currentReqPage = getRequestPage(node.id);
        const totalReqPages = Math.max(
          1,
          Math.ceil(nodeRequestsOpen.length / itemsPerPage),
        );
        const paginatedRequests = nodeRequestsOpen.slice(
          (currentReqPage - 1) * itemsPerPage,
          currentReqPage * itemsPerPage,
        );

        const latestTxDateStr = (() => {
          if (nodeTransfers.length === 0) return "—";
          const latest = nodeTransfers.reduce((max, t) => {
            const tTime = Date.parse(t.timestamp || "");
            const mTime = Date.parse(max.timestamp || "");
            return isNaN(mTime) || (!isNaN(tTime) && tTime > mTime) ? t : max;
          });
          const d = new Date(latest.timestamp);
          return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB");
        })();

        const equipmentAll = (equipmentByNode[node.id] || []).slice();
        const serialItems = equipmentAll
          .filter((x) => x.type === "serial")
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
          );
        const countItems = equipmentAll
          .filter((x) => x.type === "count")
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
          );

        return (
          <div key={node.id} className="mb-4">
            {/* Node header */}
            <div className="card shadow-sm border-0">
              <div className="card-header bg-success text-white d-flex flex-wrap justify-content-between align-items-center">
                <div className="d-flex flex-column">
                  <strong style={{ fontSize: 16 }}>{node.nodename}</strong>
                  <small className="text-light">
                    Last transaction: {latestTxDateStr}
                  </small>
                </div>

                <div className="mt-2 mt-md-0">
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => openRequestModal(node.id)}
                  >
                    + New Request
                  </button>
                </div>
              </div>

              <div className="card-body">
                <div className="row">
                  {/* LEFT: STOCK */}
                  <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Stock</h6>
                      <small className="text-muted">
                        {serialItems.length + countItems.length} items
                      </small>
                    </div>

                    <div
                      className="border rounded"
                      style={{ overflow: "hidden" }}
                    >
                      <div
                        style={{
                          maxHeight: "70vh",
                          overflowY: "auto",
                          background: "#fff",
                        }}
                      >
                        {/* Count group */}
                        <div className="px-3 py-2 border-top border-bottom bg-light d-flex justify-content-between">
                          <strong>Count items</strong>
                          <span className="text-muted small">
                            {countItems.length}
                          </span>
                        </div>

                        {countItems.length === 0 ? (
                          <div className="px-3 py-3 text-muted small">
                            No count items in this node.
                          </div>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {countItems.map((item) => (
                              <li key={item.id} className="list-group-item">
                                {renderEquipment(item, node.id)}
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Serial group */}
                        <div className="px-3 py-2 border-bottom bg-light d-flex justify-content-between">
                          <strong>Serial items</strong>
                          <span className="text-muted small">
                            {serialItems.length}
                          </span>
                        </div>

                        {serialItems.length === 0 ? (
                          <div className="px-3 py-3 text-muted small">
                            No serial items in this node.
                          </div>
                        ) : (
                          <ul className="list-group list-group-flush">
                            {serialItems.map((item) => (
                              <li key={item.id} className="list-group-item">
                                {renderEquipment(item, node.id)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: REQUESTS + TRANSACTIONS */}
                  <div className="col-12 col-lg-6">
                    {/* Requests */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Open Requests</h6>
                        <small className="text-muted">
                          {nodeRequestsOpen.length} open
                        </small>
                      </div>

                      <div
                        className="border rounded"
                        style={{ overflow: "hidden" }}
                      >
                        <div style={{ maxHeight: "34vh", overflowY: "auto" }}>
                          {paginatedRequests.length === 0 ? (
                            <div className="px-3 py-3 text-muted small">
                              No open requests.
                            </div>
                          ) : (
                            <ul className="list-group list-group-flush">
                              {paginatedRequests.map((r) => {
                                const req = Number(r.requestedQuantity || 0);
                                const ful = Number(r.fulfilledQuantity || 0);
                                const pct =
                                  r.itemType === "serial"
                                    ? r.status === "fulfilled"
                                      ? 100
                                      : 0
                                    : req > 0
                                      ? Math.min(
                                          100,
                                          Math.round((ful / req) * 100),
                                        )
                                      : 0;

                                return (
                                  <li key={r.id} className="list-group-item">
                                    <div className="d-flex justify-content-between align-items-start flex-wrap">
                                      <div
                                        style={{
                                          minWidth: 220,
                                          paddingRight: 10,
                                        }}
                                      >
                                        <div className="d-flex align-items-center flex-wrap">
                                          <strong className="mr-2">
                                            {r.name}
                                          </strong>
                                          <span className="badge badge-light mr-2">
                                            {r.itemType === "serial"
                                              ? "Serial"
                                              : "Count"}
                                          </span>
                                          <span className="text-muted small">
                                            {r.itemType === "serial"
                                              ? "Qty: 1"
                                              : `Qty: ${ful}/${req}`}
                                          </span>
                                        </div>

                                        {/* progress */}
                                        <div className="mt-2">
                                          <div
                                            className="progress"
                                            style={{ height: 8 }}
                                          >
                                            <div
                                              className="progress-bar"
                                              role="progressbar"
                                              style={{ width: `${pct}%` }}
                                              aria-valuenow={pct}
                                              aria-valuemin="0"
                                              aria-valuemax="100"
                                            />
                                          </div>
                                          <div className="d-flex justify-content-between mt-1">
                                            <small className="text-muted">
                                              {new Date(
                                                r.timestamp,
                                              ).toLocaleString("en-GB")}
                                            </small>
                                            <small className="text-muted">
                                              {r.createdBy || "Unknown"}
                                            </small>
                                          </div>
                                        </div>

                                        {r.note ? (
                                          <div className="small text-muted mt-1">
                                            Note: {r.note}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div
                                        className="d-flex flex-wrap justify-content-end mt-2 mt-md-0"
                                        style={{ gap: 6 }}
                                      >
                                        <button
                                          className="btn btn-sm btn-outline-secondary py-0 px-3"
                                          title="Mark as fulfilled"
                                          onClick={async () => {
                                            await updateDoc(
                                              doc(
                                                db,
                                                "equipmentRequests",
                                                r.id,
                                              ),
                                              {
                                                fulfilledQuantity:
                                                  r.requestedQuantity,
                                                status: "fulfilled",
                                              },
                                            );
                                            await fetchData();
                                          }}
                                        >
                                          Fulfilled
                                        </button>

                                        <button
                                          className="btn btn-sm btn-outline-danger py-0 px-3"
                                          onClick={() => cancelRequest(r.id)}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>

                        {/* request pagination */}
                        {nodeRequestsOpen.length > itemsPerPage && (
                          <div className="border-top p-2 d-flex justify-content-center">
                            <div className="d-flex align-items-center">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentReqPage <= 1}
                                onClick={() =>
                                  updateRequestPage(
                                    node.id,
                                    Math.max(1, currentReqPage - 1),
                                  )
                                }
                              >
                                Prev
                              </button>
                              <div className="px-2 small text-muted">
                                Page {currentReqPage} / {totalReqPages}
                              </div>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentReqPage >= totalReqPages}
                                onClick={() =>
                                  updateRequestPage(
                                    node.id,
                                    Math.min(totalReqPages, currentReqPage + 1),
                                  )
                                }
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Transactions */}
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Recent Transactions</h6>
                        <small className="text-muted">
                          {nodeTransfers.length} total
                        </small>
                      </div>

                      <div
                        className="border rounded"
                        style={{ overflow: "hidden" }}
                      >
                        <div style={{ maxHeight: "34vh", overflowY: "auto" }}>
                          {paginatedTransfers.length === 0 ? (
                            <div className="px-3 py-3 text-muted small">
                              No transactions yet.
                            </div>
                          ) : (
                            <ul className="list-group list-group-flush">
                              {paginatedTransfers.map((t) => (
                                <li key={t.id} className="list-group-item">
                                  <div className="d-flex justify-content-between align-items-start flex-wrap">
                                    <div
                                      style={{
                                        minWidth: 220,
                                        paddingRight: 10,
                                      }}
                                    >
                                      <div className="mb-1">
                                        <strong>{t.name}</strong>
                                      </div>
                                      <div className="small">
                                        <span className="text-muted">
                                          From:
                                        </span>{" "}
                                        <strong>{t.fromName}</strong>{" "}
                                        <span className="text-muted">
                                          → To:
                                        </span>{" "}
                                        <strong>{t.toName}</strong>
                                      </div>
                                      <div className="small text-muted mt-1">
                                        Qty: <strong>{t.quantity}</strong>
                                        {t.note ? <> · Note: {t.note}</> : null}
                                      </div>
                                      <div className="small text-muted mt-1">
                                        {new Date(t.timestamp).toLocaleString(
                                          "en-GB",
                                        )}{" "}
                                        — {t.createdBy || "Unknown"}
                                      </div>
                                    </div>

                                    <div className="mt-2 mt-md-0">
                                      <button
                                        className="btn btn-sm btn-outline-danger py-0 px-3"
                                        onClick={async () => {
                                          const confirmDelete = window.confirm(
                                            "Delete this transaction?",
                                          );
                                          if (!confirmDelete) return;
                                          await deleteDoc(
                                            doc(db, "equipmentTransfers", t.id),
                                          );
                                          await fetchData();
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {/* tx pagination */}
                        {nodeTransfers.length > itemsPerPage && (
                          <div className="border-top p-2 d-flex justify-content-center">
                            <div className="d-flex align-items-center">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentTxPage <= 1}
                                onClick={() =>
                                  updateNodePage(
                                    node.id,
                                    Math.max(1, currentTxPage - 1),
                                  )
                                }
                              >
                                Prev
                              </button>
                              <div className="px-2 small text-muted">
                                Page {currentTxPage} / {totalTxPages}
                              </div>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentTxPage >= totalTxPages}
                                onClick={() =>
                                  updateNodePage(
                                    node.id,
                                    Math.min(totalTxPages, currentTxPage + 1),
                                  )
                                }
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {/* Transfer Modal (unchanged UI, wired to autoFulfill) */}
      {showModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Transfer Equipment</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  style={{
                    fontSize: "1.25rem",
                    lineHeight: "1",
                    padding: "0.25rem 0.4rem",
                  }}
                  onClick={() => setShowModal(false)}
                >
                  X
                </button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>{transferData.name}</strong>
                  <br />
                  {transferData.itemType === "serial"
                    ? `Serial Number: ${transferData.serialNumber || "-"}`
                    : `Current Quantity: ${transferData.currentQuantity}`}
                </p>

                <label>Transfer to Node:</label>
                <select
                  className="form-control mb-2"
                  value={transferData.to}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedNode = nodes.find((n) => n.id === selectedId);

                    setTransferData({
                      ...transferData,
                      to: selectedId,
                      toName: selectedNode ? selectedNode.nodename : "",
                    });
                  }}
                >
                  <option value="">-- Select Node --</option>
                  {nodes
                    .filter((n) => n.id !== transferData.from)
                    .map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.nodename}
                      </option>
                    ))}
                </select>

                <label>Quantity to Transfer:</label>
                <input
                  type="number"
                  min="1"
                  className="form-control mb-2"
                  value={transferData.quantity}
                  onChange={(e) =>
                    setTransferData({
                      ...transferData,
                      quantity:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value, 10),
                    })
                  }
                />

                <label>Transfer Note:</label>
                <input
                  className="form-control mb-2"
                  value={transferData.note || ""}
                  onChange={(e) =>
                    setTransferData({ ...transferData, note: e.target.value })
                  }
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleTransfer}
                >
                  Confirm Transfer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal (calls autoFulfill after creating) */}
      {showCreateModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Equipment</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  style={{
                    fontSize: "1.25rem",
                    lineHeight: "1",
                    padding: "0.25rem 0.4rem",
                  }}
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewEquipment({
                      node: "",
                      name: "",
                      type: "count",
                      quantity: 1,
                      serialNumber: "",
                      note: "",
                    });
                  }}
                >
                  X
                </button>
              </div>
              <div className="modal-body">
                <label>Node:</label>
                <select
                  className="form-control mb-2"
                  value={newEquipment.node}
                  onChange={(e) =>
                    setNewEquipment({ ...newEquipment, node: e.target.value })
                  }
                >
                  <option value="">-- Select Node --</option>
                  {nodes.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.nodename}
                    </option>
                  ))}
                </select>

                <label>Name:</label>
                <input
                  list="equipment-name-options"
                  className="form-control mb-2"
                  value={newEquipment.name}
                  onChange={(e) => {
                    const inputName = e.target.value;
                    const allEquipment = Object.values(equipmentByNode).flat();
                    const matched = allEquipment.find(
                      (item) => item.name === inputName,
                    );

                    setNewEquipment({
                      ...newEquipment,
                      name: inputName,
                      type: matched ? matched.type : newEquipment.type,
                    });
                  }}
                />
                <datalist id="equipment-name-options">
                  {[
                    ...new Map(
                      Object.values(equipmentByNode)
                        .flat()
                        .sort((a, b) => {
                          if (a.type !== b.type)
                            return a.type === "count" ? -1 : 1;
                          return a.name.localeCompare(b.name);
                        })
                        .map((e) => [e.name, e]),
                    ).values(),
                  ].map((e, idx) => (
                    <option key={idx} value={e.name} />
                  ))}
                </datalist>

                <label>Type:</label>
                <select
                  className="form-control mb-2"
                  value={newEquipment.type}
                  onChange={(e) =>
                    setNewEquipment({ ...newEquipment, type: e.target.value })
                  }
                >
                  <option value="count">Count</option>
                  <option value="serial">Serial</option>
                </select>

                {newEquipment.type === "serial" ? (
                  <>
                    <label>Serial Number:</label>
                    <input
                      className="form-control"
                      value={newEquipment.serialNumber}
                      onChange={(e) =>
                        setNewEquipment({
                          ...newEquipment,
                          serialNumber: e.target.value,
                        })
                      }
                    />
                  </>
                ) : (
                  <>
                    <label>Quantity:</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      value={newEquipment.quantity}
                      onChange={(e) =>
                        setNewEquipment({
                          ...newEquipment,
                          quantity:
                            e.target.value === ""
                              ? ""
                              : parseInt(e.target.value, 10),
                        })
                      }
                    />
                  </>
                )}
                <label>Note:</label>
                <input
                  className="form-control mb-2"
                  value={newEquipment.note || ""}
                  onChange={(e) =>
                    setNewEquipment({ ...newEquipment, note: e.target.value })
                  }
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewEquipment({
                      node: "",
                      name: "",
                      type: "count",
                      quantity: 1,
                      serialNumber: "",
                      note: "",
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateEquipment}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Count Modal (calls autoFulfill after add) */}
      {showAddCountModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Quantity</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  style={{
                    fontSize: "1.25rem",
                    lineHeight: "1",
                    padding: "0.25rem 0.4rem",
                  }}
                  onClick={() => setShowAddCountModal(false)}
                >
                  X
                </button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>{addCountData.name}</strong>
                  <br />
                  Current Quantity: {addCountData.currentQuantity}
                </p>
                <label>Quantity to Add:</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  value={addCountData.quantityToAdd}
                  onChange={(e) =>
                    setAddCountData({
                      ...addCountData,
                      quantityToAdd:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value, 10),
                    })
                  }
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddCountModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    const {
                      nodeId,
                      equipmentId,
                      currentQuantity,
                      quantityToAdd,
                      name,
                    } = addCountData;

                    const qty = Number(quantityToAdd) || 0;
                    if (qty <= 0) return;

                    const itemRef = doc(
                      db,
                      `nodes/${nodeId}/sleepstudyequipment/${equipmentId}`,
                    );
                    await updateDoc(itemRef, {
                      quantity: currentQuantity + qty,
                    });

                    await addDoc(collection(db, "equipmentTransfers"), {
                      equipmentId,
                      from: "Center",
                      to: nodeId,
                      quantity: qty,
                      note: "Manual Add",
                      timestamp: new Date().toISOString(),
                      createdBy: currentUser.displayName || "",
                      fromName: "Center",
                      toName: nodes.find((n) => n.id === nodeId).nodename || "",
                      name: name,
                    });

                    // ✅ NEW: auto-fulfill requests
                    await autoFulfillRequests({
                      nodeId,
                      name,
                      itemType: "count",
                      addedQuantity: qty,
                    });

                    setShowAddCountModal(false);
                    await fetchData();
                  }}
                >
                  Confirm Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scrap/Consume Modal (unchanged logic) */}
      {showReleaseConsumeModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {releaseConsumeData.type === "scrap" ? "Scrap" : "Consume"}{" "}
                  Equipment
                </h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  style={{
                    fontSize: "1.25rem",
                    lineHeight: "1",
                    padding: "0.25rem 0.4rem",
                  }}
                  onClick={() => setShowReleaseConsumeModal(false)}
                >
                  X
                </button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>{releaseConsumeData.name}</strong>
                  <br />
                  {releaseConsumeData.itemType === "serial"
                    ? `Serial Number: ${releaseConsumeData.serialNumber || "-"}`
                    : `Current Quantity: ${releaseConsumeData.currentQuantity}`}
                </p>

                <label>Action Type:</label>
                <select
                  className="form-control mb-2"
                  value={releaseConsumeData.type}
                  onChange={(e) =>
                    setReleaseConsumeData({
                      ...releaseConsumeData,
                      type: e.target.value,
                    })
                  }
                >
                  <option value="consume">Consume</option>
                  <option value="scrap">Scrap</option>
                </select>

                {releaseConsumeData.itemType === "count" && (
                  <>
                    <label>Quantity:</label>
                    <input
                      type="number"
                      min="1"
                      max={releaseConsumeData.currentQuantity}
                      className="form-control mb-2"
                      value={releaseConsumeData.quantity}
                      onChange={(e) =>
                        setReleaseConsumeData({
                          ...releaseConsumeData,
                          quantity:
                            e.target.value === ""
                              ? ""
                              : parseInt(e.target.value || "1", 10),
                        })
                      }
                    />
                  </>
                )}

                <label>Note:</label>
                <input
                  className="form-control mb-2"
                  value={releaseConsumeData.note || ""}
                  onChange={(e) =>
                    setReleaseConsumeData({
                      ...releaseConsumeData,
                      note: e.target.value,
                    })
                  }
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowReleaseConsumeModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    const {
                      nodeId,
                      equipmentId,
                      quantity,
                      currentQuantity,
                      name,
                      type,
                      itemType,
                    } = releaseConsumeData;

                    const ref = doc(
                      db,
                      `nodes/${nodeId}/sleepstudyequipment/${equipmentId}`,
                    );

                    if (itemType === "serial") {
                      await deleteDoc(ref);
                    } else {
                      await updateDoc(ref, {
                        quantity: Math.max(
                          0,
                          currentQuantity - Number(quantity || 0),
                        ),
                      });
                    }

                    await addDoc(collection(db, "equipmentTransfers"), {
                      equipmentId,
                      from: nodeId,
                      to: type === "scrap" ? "Scrapped" : "Consumed",
                      quantity:
                        itemType === "serial" ? 1 : Number(quantity || 0),
                      note: releaseConsumeData.note || type,
                      timestamp: new Date().toISOString(),
                      createdBy: currentUser.displayName || "",
                      fromName:
                        nodes.find((n) => n.id === nodeId).nodename || "",
                      toName: type === "scrap" ? "Scrapped" : "Consumed",
                      name,
                    });

                    setShowReleaseConsumeModal(false);
                    await fetchData();
                  }}
                >
                  Confirm{" "}
                  {releaseConsumeData.type === "scrap" ? "Scrap" : "Consume"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: Request Equipment Modal */}
      {showRequestModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Request Equipment</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  style={{
                    fontSize: "1.25rem",
                    lineHeight: "1",
                    padding: "0.25rem 0.4rem",
                  }}
                  onClick={() => setShowRequestModal(false)}
                >
                  X
                </button>
              </div>
              <div className="modal-body">
                {/* Node selection */}
                <label>Node:</label>
                <select
                  className="form-control mb-2"
                  value={requestData.nodeId}
                  onChange={(e) =>
                    setRequestData({ ...requestData, nodeId: e.target.value })
                  }
                >
                  <option value="">-- Select Node --</option>
                  {nodes.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.nodename}
                    </option>
                  ))}
                </select>

                {/* Name with datalist */}
                <label>Name:</label>
                <input
                  list="equipment-name-options"
                  className="form-control mb-2"
                  value={requestData.name}
                  onChange={(e) => {
                    const inputName = e.target.value;
                    const allEquipment = Object.values(equipmentByNode).flat();
                    const matched = allEquipment.find(
                      (item) => item.name === inputName,
                    );
                    setRequestData({
                      ...requestData,
                      name: inputName,
                      itemType: matched ? matched.type : requestData.itemType,
                    });
                  }}
                />
                <datalist id="equipment-name-options">
                  {[
                    ...new Map(
                      Object.values(equipmentByNode)
                        .flat()
                        .sort((a, b) => {
                          if (a.type !== b.type)
                            return a.type === "count" ? -1 : 1;
                          return a.name.localeCompare(b.name);
                        })
                        .map((e) => [e.name, e]), // remove duplicates by name
                    ).values(),
                  ].map((e, idx) => (
                    <option key={idx} value={e.name} />
                  ))}
                </datalist>

                {/* Type selection */}
                <label>Type:</label>
                <select
                  className="form-control mb-2"
                  value={requestData.itemType}
                  onChange={(e) =>
                    setRequestData({ ...requestData, itemType: e.target.value })
                  }
                >
                  <option value="count">Count</option>
                  <option value="serial">Serial</option>
                </select>

                {/* Quantity if count */}
                {requestData.itemType === "count" && (
                  <>
                    <label>Quantity:</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control mb-2"
                      value={requestData.quantity}
                      onChange={(e) =>
                        setRequestData({
                          ...requestData,
                          quantity:
                            e.target.value === ""
                              ? ""
                              : parseInt(e.target.value, 10),
                        })
                      }
                    />
                  </>
                )}

                {/* Note */}
                <label>Note:</label>
                <input
                  className="form-control mb-2"
                  value={requestData.note || ""}
                  onChange={(e) =>
                    setRequestData({ ...requestData, note: e.target.value })
                  }
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRequestModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateRequest}
                >
                  Create Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentOverviewPage;
